import { Component, OnInit } from '@angular/core';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { CommonModule } from '@angular/common';
import { ToastrService } from 'ngx-toastr';
import { faEye, faPencil, faTrash } from '@fortawesome/free-solid-svg-icons';

import { Table } from '../../shared/table/table';
import { Popup } from '../../../core/shared/components/popup/popup';
import { DynamicFieldConfig } from '../../../core/shared/dynamic-form/form-config.model';
import { TableColumn, TableAction } from '../../shared/models/table-models';
import { Setup } from '../shared/service/setup';

@Component({
  selector: 'app-employee-registration',
  imports: [TranslatePipe, CommonModule, Table, Popup],
  templateUrl: './employee-registration.html',
  styleUrl: './employee-registration.css',
})
export class EmployeeRegistration implements OnInit {
  faPencil = faPencil;
  faEye = faEye;
  faTrash = faTrash;

  employees: any[] = [];
  columns: TableColumn[] = [];
  fields: DynamicFieldConfig[] = [];

  showPopup = false;
  isEditMode = false;
  isViewMode = false;
  formData: any = {};

  showDeletePopup = false;
  deleteMessage = '';
  selectedRow: any = null;

  constructor(
    private translate: TranslateService,
    private setup: Setup,
    private toastr: ToastrService,
  ) {}

  // ================= INIT =================
  ngOnInit(): void {
    this.buildColumns();
    this.loadEmployees();
  }

  // ================= TABLE =================
  buildColumns() {
    this.columns = [
      { key: 'empNm', label: 'SETUP.EMPLOYEE.FULL_NAME' },
      { key: 'empCnic', label: 'SETUP.EMPLOYEE.CNIC' },
      { key: 'empLanId', label: 'SETUP.EMPLOYEE.USER_ID' },
      { key: 'hrid', label: 'SETUP.EMPLOYEE.HR_ID' },
    ];
  }

  // ================= FORM FIELDS =================
  buildFields(readonly: boolean) {
    this.fields = [
      { key: 'empSeq', label: 'SETUP.ID', type: 'text', hidden: true },
      {
        key: 'empNm',
        label: 'SETUP.EMPLOYEE.FULL_NAME',
        type: 'text',
        readonly,
        validations: { required: true, textOnly: true },
      },
      {
        key: 'empCnic',
        label: 'SETUP.EMPLOYEE.CNIC',
        type: 'text',
        readonly,
        inputmode: 'numeric',
        maxlength: 13,
        validations: { required: true, cnic: true },
      },
      {
        key: 'empLanId',
        label: 'SETUP.EMPLOYEE.USER_ID',
        type: 'text',
        readonly,
        validations: { required: true, noEdgeSpaces: true, noUppercase: true },
      },
      {
        key: 'hrid',
        label: 'SETUP.EMPLOYEE.HR_ID',
        type: 'text',
        readonly,
        inputmode: 'numeric',
        maxlength: 10,
        validations: {
          required: true,
          numbersOnly: true,
          minLength: 1,
          maxLength: 10,
          noIdenticalDigits: true,
        },
      },
    ];
  }

  // ================= ACTIONS =================
  actions: TableAction[] = [
    {
      icon: this.faPencil,
      class: 'text-indigo-600',
      tooltip: 'COMMON.EDIT',
      onClick: (row: any) => this.openPopup(row),
    },
    {
      icon: this.faEye,
      class: 'text-green-600',
      tooltip: 'COMMON.VIEW',
      onClick: (row: any) => this.openViewPopup(row),
    },
    {
      icon: this.faTrash,
      class: 'text-red-600',
      tooltip: 'COMMON.DELETE',
      onClick: (row: any) => this.openDeletePopup(row),
    },
  ];

  // ================= LOAD =================
  loadEmployees() {
    this.setup.getAllEmployees().subscribe({
      next: (res: any) => {
        const data = Array.isArray(res) ? res : res?.data || [];

        this.employees = data.map((item: any) => ({
          empSeq: item.empSeq,
          empCnic: item.empCnic,
          empNm: item.empNm,
          empLanId: item.empLanId,
          empId: item.empId,
          hrid: item.hrid,
        }));
      },
      error: () => {
        this.employees = [];
      },
    });
  }

  // ================= POPUP =================
  openPopup(data?: any) {
    this.isViewMode = false;
    this.buildFields(false);
    this.showPopup = true;

    if (data) {
      this.isEditMode = true;
      this.selectedRow = data;

      this.formData = {
        empSeq: data.empSeq,
        empNm: data.empNm,
        empCnic: data.empCnic,
        empLanId: data.empLanId,
        hrid: data.hrid,
      };
    } else {
      this.isEditMode = false;
      this.selectedRow = null;

      this.formData = {
        empSeq: null,
        empNm: '',
        empCnic: '',
        empLanId: '',
        hrid: '',
      };
    }
  }

  openViewPopup(row: any) {
    this.isEditMode = true;
    this.isViewMode = true;
    this.selectedRow = row;
    this.buildFields(true);

    this.formData = {
      empSeq: row.empSeq,
      empNm: row.empNm,
      empCnic: row.empCnic,
      empLanId: row.empLanId,
      hrid: row.hrid,
    };

    this.showPopup = true;
  }

  // ================= DELETE =================
  openDeletePopup(row: any) {
    this.selectedRow = row;
    this.deleteMessage = this.translate.instant('SETUP.CONFIRM_DELETE_NAME', { name: row.empNm });
    this.showDeletePopup = true;
  }

  confirmDelete(): void {
    if (!this.selectedRow) return;

    const payload = {
      empSeq: this.selectedRow.empSeq,
      empCnic: Number(this.selectedRow.empCnic),
      empNm: this.selectedRow.empNm,
      empLanId: this.selectedRow.empLanId,
      empId: null,
      hrid: this.selectedRow.hrid,
      delFlg: 1,
    };

    this.setup.saveEmployee(payload).subscribe({
      next: () => {
        this.toastr.success(this.translate.instant('SETUP.DELETED_SUCCESSFULLY'));
        this.showDeletePopup = false;
        this.selectedRow = null;
        this.loadEmployees();
      },
      error: () => this.toastr.error(this.translate.instant('SETUP.DELETE_FAILED')),
    });
  }

  // ================= SAVE =================
  handleSave = (form: any) => {
    const payload = {
      empSeq: this.isEditMode ? form.empSeq : null,
      empCnic: Number(form.empCnic),
      empNm: form.empNm,
      empLanId: form.empLanId,
      empId: null,
      hrid: form.hrid,
      delFlg: 0,
    };

    this.setup.saveEmployee(payload).subscribe({
      next: () => {
        this.toastr.success(this.translate.instant(this.isEditMode ? 'SETUP.UPDATED_SUCCESSFULLY' : 'SETUP.CREATED_SUCCESSFULLY'));
        this.showPopup = false;
        this.loadEmployees();
      },
      error: () => this.toastr.error(this.translate.instant('SETUP.SAVE_FAILED')),
    });
  };
}